'use client';

import { Helmet } from '@/lib/helmet-shim';
import { usePathname } from 'next/navigation';
import { CrawlerTags } from './CrawlerTags';
import { PerformanceTags } from './PerformanceTags';

const BASE_URL = 'https://www.scriptunlock.com';
const DEFAULT_IMAGE = `${BASE_URL}/og-image.jpg`;
const SITE_NAME = 'Script Unlock';

const DEFAULT_KEYWORDS = 'prescription savings, pharmacy comparison, medication prices, compounding pharmacy, prescription bids, drug prices, pharmacy finder, prescription discounts, cheap prescriptions, medication savings, pharmacy deals, Rx savings, GLP-1 medications, semaglutide, tirzepatide';

interface EnhancedSEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  imageWidth?: number;
  imageHeight?: number;
  imageAlt?: string;
  type?: 'website' | 'article' | 'product' | 'profile';
  noIndex?: boolean;
  noFollow?: boolean;
  structuredData?: object | object[];
  article?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
    section?: string;
    tags?: string[];
  };
  product?: {
    price?: string;
    currency?: string;
    availability?: 'InStock' | 'OutOfStock' | 'PreOrder';
    brand?: string;
  };
  /** Enable hreflang tags for international SEO */
  enableHreflang?: boolean;
  /** Enable performance preloads */
  enablePerformance?: boolean;
  /** Additional preconnect URLs */
  preconnectUrls?: string[];
  /** Additional prefetch URLs */
  prefetchUrls?: string[];
  /** Custom canonical URL */
  canonicalUrl?: string;
  /** Priority hint for search engines (0.0 to 1.0) */
  priority?: number;
  /** Last modified date for sitemap/cache hints */
  lastModified?: string;
}

/**
 * EnhancedSEOHead Component
 * 
 * World-class SEO implementation with:
 * - Comprehensive Open Graph tags
 * - Twitter Card support with all card types
 * - Article and Product structured meta
 * - Hreflang for internationalization
 * - Performance optimization tags
 * - Advanced crawler directives
 * - Schema.org structured data
 * 
 * Use this as the primary SEO component for all pages.
 */
export const EnhancedSEOHead = ({
  title,
  description = 'Upload your prescription and licensed, verified pharmacies each respond with their own cash price. You pay the pharmacy directly. Free for patients.',
  keywords = DEFAULT_KEYWORDS,
  image = DEFAULT_IMAGE,
  imageWidth = 1200,
  imageHeight = 630,
  imageAlt,
  type = 'website',
  noIndex = false,
  noFollow = false,
  structuredData,
  article,
  product,
  enableHreflang = true,
  enablePerformance = true,
  preconnectUrls,
  prefetchUrls,
  canonicalUrl,
  priority,
  lastModified,
}: EnhancedSEOHeadProps) => {
  const pathname = usePathname();
  const currentUrl = canonicalUrl || `${BASE_URL}${pathname}`;
  
  // Avoid double-appending "| Script Unlock" if the title already ends with it
  const fullTitle = !title
    ? `${SITE_NAME} - Pharmacies Compete. You Save. | Free Rx Price Comparison`
    : title.endsWith(`| ${SITE_NAME}`)
      ? title
      : `${title} | ${SITE_NAME}`;

  const effectiveImageAlt = imageAlt || title || 'Script Unlock - Pharmacies Compete. You Save.';

  const structuredDataArray = Array.isArray(structuredData) 
    ? structuredData 
    : structuredData 
      ? [structuredData] 
      : [];

  return (
    <>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>{fullTitle}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={keywords} />
        <link rel="canonical" href={currentUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content={type} />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:title" content={fullTitle} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={image} />
        <meta property="og:image:width" content={String(imageWidth)} />
        <meta property="og:image:height" content={String(imageHeight)} />
        <meta property="og:image:alt" content={effectiveImageAlt} />
        <meta property="og:image:type" content={image.endsWith('.svg') ? 'image/svg+xml' : 'image/png'} />
        <meta property="og:site_name" content={SITE_NAME} />
        <meta property="og:locale" content="en_US" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={fullTitle} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={image} />
        <meta name="twitter:site" content="@script_unlock" />

        {/* Article-specific meta */}
        {article?.publishedTime && (
          <meta property="article:published_time" content={article.publishedTime} />
        )}
        {article?.modifiedTime && (
          <meta property="article:modified_time" content={article.modifiedTime} />
        )}
        {article?.author && (
          <meta property="article:author" content={article.author} />
        )}

        {/* Product-specific meta */}
        {product?.price && (
          <meta property="product:price:amount" content={product.price} />
        )}
        {product?.currency && (
          <meta property="product:price:currency" content={product.currency} />
        )}

        {/* Priority hint (for internal use) */}
        {priority !== undefined && (
          <meta name="x-page-priority" content={String(priority)} />
        )}

        {/* Structured Data */}
        {structuredDataArray.map((data, index) => (
          <script key={index} type="application/ld+json">
            {JSON.stringify(data)}
          </script>
        ))}
      </Helmet>

      {/* Crawler directives */}
      <CrawlerTags 
        index={!noIndex} 
        follow={!noFollow}
        maxImagePreview="large"
        maxSnippet={-1}
        maxVideoPreview={-1}
      />

      {/* International SEO */}

      {/* Performance optimization */}
      {enablePerformance && (
        <PerformanceTags 
          preconnectUrls={preconnectUrls}
          prefetchUrls={prefetchUrls}
        />
      )}
    </>
  );
};

export default EnhancedSEOHead;
